import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { checkEventEditAccess, getEventById } from '@/server/event';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/ui/navbar';
import { EditEventSection } from '@/components/adminEditEvent/adminEditEvent';
import { ArrowLeft } from 'lucide-react';

export const Route = createFileRoute('/event/$eventId/edit')({
  component: EditEventPage,
  beforeLoad: async ({ context, location, params }) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }

    const { hasAccess } = await checkEventEditAccess({
      data: params.eventId,
    });
    if (!hasAccess) {
      throw redirect({
        to: '/event/$eventId',
        params: { eventId: params.eventId },
        search: { unauthorized: true },
      });
    }

    return context.profile;
  },
  loader: async ({ params }) => {
    const event = await getEventById({ data: params.eventId });
    return { event };
  },
  errorComponent: ({ error }) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-6 text-center">
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Event Not Found
            </h1>
            <p className="text-gray-600 mb-4">
              {error?.message ||
                "The event you're looking for doesn't exist."}
            </p>
            <Link to="/search">
              <Button>Browse Events</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  },
});

/** Standalone event edit page for claiming/editing unclaimed events. */
function EditEventPage() {
  const { event } = Route.useLoaderData();
  const navigate = useNavigate();

  const handleSaveSuccess = async () => {
    navigate({ to: '/event/$eventId', params: { eventId: event.id } });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link
          to="/event/$eventId"
          params={{ eventId: event.id }}
          className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to event
        </Link>

        <EditEventSection event={event} onSaveSuccess={handleSaveSuccess} />
      </div>
    </div>
  );
}
